import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router";

import { Grid, Box, Card, CardContent, CardHeader, Container, Typography, LinearProgress } from "@mui/material";
import ActivityForm from "./ActivityForm";
import ActivityCategoryContent from "./ActivityCategoryContent";
import ActivityCategoryFormDialog from "./ActivityCategoryFormDialog";
import ActivityReviewContent from "./ActivityReviewContent";
import ActivityReviewFormDialog from "./ActivityReviewFormDialog";
import ActivityResourceContent from "./ActivityResourceContent";
import ActivityPDPContent from "./ActivityPDPContent";
import ActivityStatusDetails from "./ActivityStatusDetails";
import AlertMessage from "../../../Common/Alert/AlertMessage";
import AttachDevelopmentNeedFormDialog from "../DevelopmentNeed/AttachDevelopmentNeedFormDialog";
import AttachResourceFormDialog from "../Resource/AttachResourceFormDialog";
import BreadcrumbBuilder from "../BreadcrumbBuilder";
import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";
import ListItemMenu from "../../../Common/ListItemMenu";
import Error404 from "../../../Errors/Error404";
import Error500 from "../../../Errors/Error500";
import PORTAvatar from "../PORTAvatar";
import ResourceForm from "../Resource/ResourceForm";
import BreadcrumbBack from "../../../Common/Breadcrumbs/BreadcrumbBack";

import { ActionTypes } from "../../../../constants";
import { getActivityDetails, updateActivity, deleteActivity } from "../../../../actions/rmsActions";
import { getActivity, getRmsAppCode, getError } from "../../../../reducers";


function Activity({ activity, appCode, onUpdate, onDelete }) {
    const history = useHistory();
    const [openDialog, setOpenDialog] = useState(null);

    const title = activity?.title ?? "Activity";

    const activityType = activity?.type?.name || "Undefined";
    const associationCount = activity?.associations?.length || 0;

    const reviewCompleted = activity?.review?.status === "completed";
    const reviewRequired = activity?.review?.isRequired;

    let alert = {
        active: false,
        severity: "",
        message: "",
    };

    if (!reviewCompleted && reviewRequired) {
        alert.active = true;
        alert.severity = "warning";
        alert.message = "Reflection required";
    }

    const handleCloseDialog = () => setOpenDialog(null);

    const location = useLocation();
    const queryString = new URLSearchParams(location.search);
    const refererPath = queryString.get("rp");

    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    {refererPath?.length > 0 ? (
                        <BreadcrumbBack path={refererPath} />
                    ) : (
                        <BreadcrumbBuilder />
                    )}
                </Grid>

                <Grid item xs={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography={true}
                            avatar={<PORTAvatar type={"activity"} badge={associationCount > 0 ? "attached" : ""} />}
                            action={
                                <ListItemMenu
                                    onEdit={!!activity?.canUpdate && (() => setOpenDialog("update"))}
                                    onDelete={!!activity?.canDelete && (() => setOpenDialog("delete"))}
                                />
                            }
                            title={
                                <Typography component="h2" className="card-header-title" >
                                    {title}
                                </Typography>
                            }
                            subheader={
                                <Typography component="span" className="card-header-subtitle" >
                                    {activityType}
                                </Typography>
                            }
                        />
                        <CardContent>
                            <ActivityStatusDetails
                                dateStart={activity?.dateStart}
                                dateEnd={activity?.dateEnd}
                                duration={activity?.duration}
                                score={activity?.score}
                            />
                            <Box className="card-content">
                                <Typography variant="body1">
                                    {activity?.description ?? ""}
                                </Typography>
                            </Box>
                        </CardContent>

                        <ActivityCategoryContent
                            activity={activity}
                            onClick={() => setOpenDialog("categories")}
                        />

                        {alert.active && (
                            <CardContent>
                                <Box className="card-content">
                                    <AlertMessage severity={alert.severity} message={alert.message} />
                                </Box>
                            </CardContent>
                        )}
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <ActivityResourceContent
                        activity={activity}
                        onClick={() => setOpenDialog("resource")}
                        onUpload={() => setOpenDialog("resourceUpload")} // TODO: need to upload file
                        onRemove={(resource) => {
                            onUpdate(activity.id, {
                                ...activity,
                                resources: activity.resources.filter(
                                    ({ id }) => Number(id) !== Number(resource)
                                ),
                            });
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <ActivityPDPContent
                        activity={activity}
                        onClick={() => setOpenDialog("pdp")}
                    />
                </Grid>

                <Grid item xs={12}>
                    <ActivityReviewContent
                        activity={activity}
                        onClick={() => setOpenDialog("review")}
                    />
                </Grid>
            </Grid>

            {openDialog === "delete" && (
                <ConfirmDialog
                    open={true}
                    title="Delete activity"
                    content={`The following activity will be deleted: ${activity.title}`}
                    confirmButtonLabel="Delete"
                    onConfirm={() => {
                        onDelete(activity.id);
                        history.replace("/portfolio/activities");
                    }}
                    onCancel={handleCloseDialog}
                    onClose={handleCloseDialog}
                />
            )}

            {openDialog === "update" && (
                <ActivityForm
                    open={true}
                    formTitle="Edit activity"
                    data={activity}
                    onSave={(params) => onUpdate(activity.id, params)}
                    onClose={handleCloseDialog}
                    scroll="paper"
                    submitLabel={`${openDialog === "add" ? "Add" : "Save"}`}
                />
            )}

            {openDialog === "categories" && (
                <ActivityCategoryFormDialog
                    open={true}
                    formTitle="Link to categories"
                    data={activity.categories}
                    onSave={(params) => onUpdate(activity.id, {...activity, categories: params})}
                    onClose={handleCloseDialog}
                    scroll="paper"
                />
            )}

            {openDialog === "resource" && (
                <AttachResourceFormDialog
                    open={true}
                    data={activity}
                    onSave={(tempAttached) => onUpdate(activity.id, {...activity, resources: tempAttached})}
                    onClose={handleCloseDialog}
                    canUpdate={activity?.canUpdate}
                    appCode={appCode}
                />
            )}

            {openDialog === "pdp" && (
                <AttachDevelopmentNeedFormDialog
                    open={true}
                    data={activity}
                    onSave={(tempAttached) => onUpdate(activity.id, {...activity, developmentNeeds: tempAttached})}
                    onClose={handleCloseDialog}
                    canUpdate={activity?.canUpdate}
                    appCode={appCode}
                />
            )}

            {openDialog === "review" && (
                <ActivityReviewFormDialog
                    open={true}
                    formTitle="Reflection"
                    canUpdate={activity?.canUpdate}
                    data={activity}
                    onSave={(params) => onUpdate(activity.id, { ...activity, review: params })}
                    onClose={handleCloseDialog}
                    scroll="paper"
                />
            )}

            {openDialog === "resourceUpload" && (
                <ResourceForm
                    open={true}
                    onClose={handleCloseDialog}
                    scroll="paper"
                    resource={null}
                    appCode={appCode}
                />
            )}
        </Container>
    );
}

const ActivityContainer = ({ match }) => {
    const dispatch = useDispatch();
    const activity = useSelector((state) => getActivity(state, match.params.id));
    const error = useSelector((state) => getError(state, ActionTypes.GET_ACTIVITY_SUCCESS));
    const appCode = useSelector(getRmsAppCode);

    useEffect(() => {
        if (!!appCode) {
            dispatch(getActivityDetails(match.params.id, appCode));
        }
    }, [dispatch, match.params.id, appCode]);

    if (error) {
        switch (error.errorCode) {
            case 404:
                return <Error404 />;
            case 500:
                return <Error500 />;
            default:
                return <div>Unknown error</div>;
        }
    }

    if (!activity) return <LinearProgress />;

    return (
        <Activity
            activity={activity}
            appCode={appCode}
            onUpdate={(id, params) => dispatch(updateActivity(appCode, id, params))}
            onDelete={(id) => dispatch(deleteActivity(appCode, id))}
        />
    );
};

export default ActivityContainer;
